/**
 * Canonical polar maths for the Polar visualiser.
 *
 * Conventions:
 *   - airspeed v in km/h
 *   - sink rate w in m/s
 *   - PolarCoeffs and all maths use POSITIVE-sinking (w > 0 means going down)
 *   - PolarPoint.w_ms uses NEGATIVE-sinking, matching XCSoar's display convention
 *     (so a raw XCSoar row drops straight into PolarPoint without conversion).
 *     `fitQuadratic` flips the sign internally before solving.
 */

export type PolarPoint = {
  readonly v_kmh: number;
  readonly w_ms: number;
};

export type PolarCoeffs = {
  readonly a: number;
  readonly b: number;
  readonly c: number;
};

export type SpeedSink = {
  readonly v_kmh: number;
  readonly w_ms: number;
};

export type BestLD = SpeedSink & { readonly ld: number };

export type XCSpeeds = {
  readonly vAir_kmh: number;
  readonly vGround_kmh: number;
};

export type ThreePoints = readonly [PolarPoint, PolarPoint, PolarPoint];

/**
 * Fit a quadratic w(v) = a·v² + b·v + c through three (v, w) points.
 * Inputs follow the XCSoar/display convention (w_ms negative when sinking);
 * outputs follow the maths convention (positive-sinking).
 *
 * Returns `null` for any of:
 *   - non-distinct airspeeds (division by zero in the elimination), or
 *   - a fit that is not concave-up (`a ≤ 0`) — i.e. the curve bends the wrong way.
 *
 * Algorithm: Gaussian elimination via successive subtraction, equivalent to
 * Cramer's rule over the 3×3 Vandermonde system.
 */
export const fitQuadratic = (points: ThreePoints): PolarCoeffs | null => {
  const [p1, p2, p3] = points;
  const v1 = p1.v_kmh;
  const v2 = p2.v_kmh;
  const v3 = p3.v_kmh;
  const w1 = -p1.w_ms;
  const w2 = -p2.w_ms;
  const w3 = -p3.w_ms;

  const d21 = v2 - v1;
  const d31 = v3 - v1;
  const d32 = v3 - v2;
  if (d21 === 0 || d31 === 0 || d32 === 0) return null;

  const slope2 = (w2 - w1) / d21;
  const slope3 = (w3 - w1) / d31;
  const a = (slope3 - slope2) / d32;
  if (a <= 0) return null;

  const b = slope2 - (v2 + v1) * a;
  const c = w1 - v1 * v1 * a - v1 * b;
  return { a, b, c };
};

/**
 * Apply wing-loading factor `k = √(m_total / m_ref)` and a bugs percentage to a polar.
 *
 *   Ballast: (v, w) → (k·v, k·w)  =>  a' = a/k, b' = b, c' = c·k
 *   Bugs:    w → w · (1 + bugs/100), i.e. uniform scale of all three coefficients.
 */
export const effectiveCoeffs = (
  base: PolarCoeffs,
  k: number,
  bugsPercent: number,
): PolarCoeffs => {
  const f = 1 + bugsPercent / 100;
  return {
    a: (base.a / k) * f,
    b: base.b * f,
    c: base.c * k * f,
  };
};

/** w(v) — sink rate at airspeed v, positive-sinking convention. */
export const sinkAt = ({ a, b, c }: PolarCoeffs, v_kmh: number): number =>
  a * v_kmh * v_kmh + b * v_kmh + c;

/** Minimum sink: vertex of the parabola at v = -b/(2a). */
export const minSink = (coeffs: PolarCoeffs): SpeedSink => {
  const v_kmh = -coeffs.b / (2 * coeffs.a);
  return { v_kmh, w_ms: sinkAt(coeffs, v_kmh) };
};

/**
 * Best L/D: tangent from the origin. v = √(c/a); L/D = (v/3.6) / w(v).
 */
export const bestLD = (coeffs: PolarCoeffs): BestLD => {
  const v_kmh = Math.sqrt(coeffs.c / coeffs.a);
  const w_ms = sinkAt(coeffs, v_kmh);
  const ld = v_kmh / 3.6 / w_ms;
  return { v_kmh, w_ms, ld };
};

/**
 * MacCready speed-to-fly.
 *   Tangent from `(HW, −MC)` to the polar gives
 *     v_stf = HW + √( HW² + (b·HW + c + MC) / a )
 * Returns `null` when the discriminant is negative — no real tangent exists.
 */
export const speedToFly = (
  coeffs: PolarCoeffs,
  mc_ms: number,
  hw_kmh: number,
): SpeedSink | null => {
  const { a, b, c } = coeffs;
  const disc = hw_kmh * hw_kmh + (b * hw_kmh + c + mc_ms) / a;
  if (disc < 0) return null;
  const v_kmh = hw_kmh + Math.sqrt(disc);
  return { v_kmh, w_ms: sinkAt(coeffs, v_kmh) };
};

/**
 * Expected cross-country speed: where the MacCready tangent crosses the X-axis.
 * `stf` must be the result of `speedToFly` for the same `mc_ms`/`hw_kmh`.
 *
 *   v_xc_air    = HW + MC · (v_stf − HW) / (w_stf + MC)
 *   v_xc_ground = v_xc_air − HW
 */
export const xcSpeed = (
  stf: SpeedSink,
  mc_ms: number,
  hw_kmh: number,
): XCSpeeds => {
  const vAir_kmh =
    hw_kmh + (mc_ms * (stf.v_kmh - hw_kmh)) / (stf.w_ms + mc_ms);
  return { vAir_kmh, vGround_kmh: vAir_kmh - hw_kmh };
};
